import { fg } from '@atlaskit/platform-feature-flags';
import { type EntityATI } from '@atlassian/search-client';

import { ThirdPartyConfigs } from '../../constants/3p-product-configs';
import {
	CONFLUENCE_ANSWER_ENTITY_ATI,
	CONFLUENCE_CALENDAR_ENTITY_ATI,
	CONFLUENCE_GLIFFY_ENTITY_ATI,
	CONFLUENCE_QUESTION_ENTITY_ATI,
	CONFLUENCE_SPACE_CALENDAR_ENTITY_ATI,
	JIRA_BOARD_ENTITY_ATI,
	JIRA_DASHBOARD_ENTITY_ATI,
	JIRA_FILTER_ENTITY_ATI,
	JIRA_ISSUE_ENTITY_ATI,
	JIRA_PLAN_ENTITY_ATI,
	JIRA_PROJECT_ENTITY_ATI,
} from '../../constants/entities';
import type { TypeFilterMapping, UniversalTypeFilterMapping } from '../../constants/filters/types';
import {
	allTypeFilterValueKeys,
	type TypeFilterConfig3P,
	TypeFilterValueKey,
} from '../../constants/filters/universal-type';
import { ProductKeys, type ProductKeys1P } from '../../constants/products';

export type TypeFilterProductMapping1P = Map<TypeFilterValueKey, EntityATI[]>;
/**
 * A mapping between 1P Products
 * - ProductKey1P e.g. 'confluence'
 *
 * UI values for the type filter
 * - TypeFilterKey e.g. 'Spreadsheet'
 *
 * and entities to request from Aggregator
 *
 * - ATIString[] e.g. ['ati:cloud:confluence:blogpost']
 */
const typeFilter1PMappingNewWithJiraSpaces = new Map<ProductKeys1P, TypeFilterProductMapping1P>([
	[
		ProductKeys.Confluence,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[
				TypeFilterValueKey.Document,
				[
					'ati:cloud:confluence:blogpost',
					'ati:cloud:confluence:page',
					'ati:cloud:confluence:attachment',
				],
			],
			[TypeFilterValueKey.Page, ['ati:cloud:confluence:page']],
			[TypeFilterValueKey.Blogpost, ['ati:cloud:confluence:blogpost']],
			[TypeFilterValueKey.Attachment, ['ati:cloud:confluence:attachment']],
			[TypeFilterValueKey.Space, ['ati:cloud:confluence:space']],
			[TypeFilterValueKey.Comment, ['ati:cloud:confluence:comment']],
			[TypeFilterValueKey.Whiteboard, ['ati:cloud:confluence:whiteboard']],
			[TypeFilterValueKey.Database, ['ati:cloud:confluence:database']],
			[TypeFilterValueKey.Embed, ['ati:cloud:confluence:embed']],
			[TypeFilterValueKey.Folder, ['ati:cloud:confluence:folder']],
			[TypeFilterValueKey.Calendar, [CONFLUENCE_CALENDAR_ENTITY_ATI]],
			[TypeFilterValueKey.SpaceCalendar, [CONFLUENCE_SPACE_CALENDAR_ENTITY_ATI]],
			[TypeFilterValueKey.Question, [CONFLUENCE_QUESTION_ENTITY_ATI]],
			[TypeFilterValueKey.Answer, [CONFLUENCE_ANSWER_ENTITY_ATI]],
			[TypeFilterValueKey.Gliffy, [CONFLUENCE_GLIFFY_ENTITY_ATI]],
		]),
	],
	[
		ProductKeys.Jira,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Issue, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Epic, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Bug, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Story, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Task, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Subtask, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Space, [JIRA_PROJECT_ENTITY_ATI]],
			[TypeFilterValueKey.Filter, [JIRA_FILTER_ENTITY_ATI]],
			[TypeFilterValueKey.Plan, [JIRA_PLAN_ENTITY_ATI]],
			[TypeFilterValueKey.Board, [JIRA_BOARD_ENTITY_ATI]],
			[TypeFilterValueKey.Dashboard, [JIRA_DASHBOARD_ENTITY_ATI]],
		]),
	],
	[
		ProductKeys.JiraProductDiscovery,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Issue, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Space, [JIRA_PROJECT_ENTITY_ATI]],
			[TypeFilterValueKey.Filter, [JIRA_FILTER_ENTITY_ATI]],
			[TypeFilterValueKey.Plan, [JIRA_PLAN_ENTITY_ATI]],
			[TypeFilterValueKey.Board, [JIRA_BOARD_ENTITY_ATI]],
			[TypeFilterValueKey.Dashboard, [JIRA_DASHBOARD_ENTITY_ATI]],
		]),
	],
	[
		ProductKeys.JiraServiceManagement,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Issue, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Incident, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Space, [JIRA_PROJECT_ENTITY_ATI]],
			[TypeFilterValueKey.Filter, [JIRA_FILTER_ENTITY_ATI]],
			[TypeFilterValueKey.Plan, [JIRA_PLAN_ENTITY_ATI]],
			[TypeFilterValueKey.Board, [JIRA_BOARD_ENTITY_ATI]],
			[TypeFilterValueKey.Dashboard, [JIRA_DASHBOARD_ENTITY_ATI]],
		]),
	],
	[
		ProductKeys.Compass,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Component, ['ati:cloud:compass:component']],
		]),
	],
	[
		ProductKeys.Focus,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[
				TypeFilterValueKey.FocusArea,
				['ati:cloud:mercury:focus-area', 'ati:cloud:mercury:focus-area-status-update'],
			],
		]),
	],
	[
		ProductKeys.Talent,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Position, ['ati:cloud:radar:position']],
		]),
	],
	[
		ProductKeys.Trello,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Card, ['ati:cloud:trello:card']],
		]),
	],
	[
		ProductKeys.Atlas,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Project, ['ati:cloud:townsquare:project']],
			[TypeFilterValueKey.Goal, ['ati:cloud:townsquare:goal']],
			[TypeFilterValueKey.Tag, ['ati:cloud:townsquare:tag']],
		]),
	],
	[
		ProductKeys.Bitbucket,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Commit, ['ati:cloud:graph:commit']],
			[TypeFilterValueKey.PullRequest, ['ati:cloud:graph:pull-request']],
			[TypeFilterValueKey.Repository, ['ati:cloud:graph:repository']],
			[TypeFilterValueKey.Branch, ['ati:cloud:graph:branch']],
		]),
	],
	[
		ProductKeys.Assets,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.AssetsObject, ['ati:cloud:cmdb:object']],
			[TypeFilterValueKey.Schema, ['ati:cloud:cmdb:schema']],
			[TypeFilterValueKey.Type, ['ati:cloud:cmdb:type']],
		]),
	],
]);

/**
 * A mapping between 1P Products
 * - ProductKey1P e.g. 'confluence'
 *
 * UI values for the type filter
 * - TypeFilterKey e.g. 'Spreadsheet'
 *
 * and entities to request from Aggregator
 *
 * - ATIString[] e.g. ['ati:cloud:confluence:blogpost']
 */
const typeFilter1PMappingNew = new Map<ProductKeys1P, TypeFilterProductMapping1P>([
	[
		ProductKeys.Confluence,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[
				TypeFilterValueKey.Document,
				[
					'ati:cloud:confluence:blogpost',
					'ati:cloud:confluence:page',
					'ati:cloud:confluence:attachment',
				],
			],
			[TypeFilterValueKey.Page, ['ati:cloud:confluence:page']],
			[TypeFilterValueKey.Blogpost, ['ati:cloud:confluence:blogpost']],
			[TypeFilterValueKey.Attachment, ['ati:cloud:confluence:attachment']],
			[TypeFilterValueKey.Space, ['ati:cloud:confluence:space']],
			[TypeFilterValueKey.Comment, ['ati:cloud:confluence:comment']],
			[TypeFilterValueKey.Whiteboard, ['ati:cloud:confluence:whiteboard']],
			[TypeFilterValueKey.Database, ['ati:cloud:confluence:database']],
			[TypeFilterValueKey.Embed, ['ati:cloud:confluence:embed']],
			[TypeFilterValueKey.Folder, ['ati:cloud:confluence:folder']],
			[TypeFilterValueKey.Calendar, [CONFLUENCE_CALENDAR_ENTITY_ATI]],
			[TypeFilterValueKey.SpaceCalendar, [CONFLUENCE_SPACE_CALENDAR_ENTITY_ATI]],
			[TypeFilterValueKey.Question, [CONFLUENCE_QUESTION_ENTITY_ATI]],
			[TypeFilterValueKey.Answer, [CONFLUENCE_ANSWER_ENTITY_ATI]],
			[TypeFilterValueKey.Gliffy, [CONFLUENCE_GLIFFY_ENTITY_ATI]],
		]),
	],
	[
		ProductKeys.Jira,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Issue, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Epic, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Bug, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Story, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Task, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Subtask, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Project, [JIRA_PROJECT_ENTITY_ATI]],
			[TypeFilterValueKey.Filter, [JIRA_FILTER_ENTITY_ATI]],
			[TypeFilterValueKey.Plan, [JIRA_PLAN_ENTITY_ATI]],
			[TypeFilterValueKey.Board, [JIRA_BOARD_ENTITY_ATI]],
			[TypeFilterValueKey.Dashboard, [JIRA_DASHBOARD_ENTITY_ATI]],
		]),
	],
	[
		ProductKeys.JiraProductDiscovery,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Issue, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Project, [JIRA_PROJECT_ENTITY_ATI]],
			[TypeFilterValueKey.Filter, [JIRA_FILTER_ENTITY_ATI]],
			[TypeFilterValueKey.Plan, [JIRA_PLAN_ENTITY_ATI]],
			[TypeFilterValueKey.Board, [JIRA_BOARD_ENTITY_ATI]],
			[TypeFilterValueKey.Dashboard, [JIRA_DASHBOARD_ENTITY_ATI]],
		]),
	],
	[
		ProductKeys.JiraServiceManagement,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Issue, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Incident, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Project, [JIRA_PROJECT_ENTITY_ATI]],
			[TypeFilterValueKey.Filter, [JIRA_FILTER_ENTITY_ATI]],
			[TypeFilterValueKey.Plan, [JIRA_PLAN_ENTITY_ATI]],
			[TypeFilterValueKey.Board, [JIRA_BOARD_ENTITY_ATI]],
			[TypeFilterValueKey.Dashboard, [JIRA_DASHBOARD_ENTITY_ATI]],
		]),
	],
	[
		ProductKeys.Compass,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Component, ['ati:cloud:compass:component']],
		]),
	],
	[
		ProductKeys.Focus,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[
				TypeFilterValueKey.FocusArea,
				['ati:cloud:mercury:focus-area', 'ati:cloud:mercury:focus-area-status-update'],
			],
		]),
	],
	[
		ProductKeys.Talent,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Position, ['ati:cloud:radar:position']],
		]),
	],
	[
		ProductKeys.Trello,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Card, ['ati:cloud:trello:card']],
		]),
	],
	[
		ProductKeys.Atlas,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Project, ['ati:cloud:townsquare:project']],
			[TypeFilterValueKey.Goal, ['ati:cloud:townsquare:goal']],
			[TypeFilterValueKey.Tag, ['ati:cloud:townsquare:tag']],
		]),
	],
	[
		ProductKeys.Bitbucket,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Commit, ['ati:cloud:graph:commit']],
			[TypeFilterValueKey.PullRequest, ['ati:cloud:graph:pull-request']],
			[TypeFilterValueKey.Repository, ['ati:cloud:graph:repository']],
			[TypeFilterValueKey.Branch, ['ati:cloud:graph:branch']],
		]),
	],
	[
		ProductKeys.Assets,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.AssetsObject, ['ati:cloud:cmdb:object']],
			[TypeFilterValueKey.Schema, ['ati:cloud:cmdb:schema']],
			[TypeFilterValueKey.Type, ['ati:cloud:cmdb:type']],
		]),
	],
]);

const typeFilter1PMappingOld = new Map<ProductKeys1P, TypeFilterProductMapping1P>([
	[
		ProductKeys.Confluence,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[
				TypeFilterValueKey.Document,
				[
					'ati:cloud:confluence:blogpost',
					'ati:cloud:confluence:page',
					'ati:cloud:confluence:attachment',
				],
			],
			[TypeFilterValueKey.Page, ['ati:cloud:confluence:page']],
			[TypeFilterValueKey.Blogpost, ['ati:cloud:confluence:blogpost']],
			[TypeFilterValueKey.Attachment, ['ati:cloud:confluence:attachment']],
			[TypeFilterValueKey.Space, ['ati:cloud:confluence:space']],
			[TypeFilterValueKey.Comment, ['ati:cloud:confluence:comment']],
			[TypeFilterValueKey.Whiteboard, ['ati:cloud:confluence:whiteboard']],
			[TypeFilterValueKey.Database, ['ati:cloud:confluence:database']],
			[TypeFilterValueKey.Embed, ['ati:cloud:confluence:embed']],
			[TypeFilterValueKey.Folder, ['ati:cloud:confluence:folder']],
			[TypeFilterValueKey.Calendar, [CONFLUENCE_CALENDAR_ENTITY_ATI]],
			[TypeFilterValueKey.SpaceCalendar, [CONFLUENCE_SPACE_CALENDAR_ENTITY_ATI]],
			[TypeFilterValueKey.Question, [CONFLUENCE_QUESTION_ENTITY_ATI]],
			[TypeFilterValueKey.Answer, [CONFLUENCE_ANSWER_ENTITY_ATI]],
			[TypeFilterValueKey.Gliffy, [CONFLUENCE_GLIFFY_ENTITY_ATI]],
		]),
	],
	[
		ProductKeys.Jira,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Issue, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Epic, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Bug, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Story, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Task, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Subtask, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Project, [JIRA_PROJECT_ENTITY_ATI]],
		]),
	],
	[
		ProductKeys.JiraProductDiscovery,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Issue, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Project, [JIRA_PROJECT_ENTITY_ATI]],
		]),
	],
	[
		ProductKeys.JiraServiceManagement,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Issue, [JIRA_ISSUE_ENTITY_ATI]],
			[TypeFilterValueKey.Project, [JIRA_PROJECT_ENTITY_ATI]],
			[TypeFilterValueKey.Incident, [JIRA_ISSUE_ENTITY_ATI]],
		]),
	],
	[
		ProductKeys.Compass,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Component, ['ati:cloud:compass:component']],
		]),
	],
	[
		ProductKeys.Focus,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[
				TypeFilterValueKey.FocusArea,
				['ati:cloud:mercury:focus-area', 'ati:cloud:mercury:focus-area-status-update'],
			],
		]),
	],
	[
		ProductKeys.Talent,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Position, ['ati:cloud:radar:position']],
		]),
	],
	[
		ProductKeys.Trello,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Card, ['ati:cloud:trello:card']],
		]),
	],
	[
		ProductKeys.Atlas,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Project, ['ati:cloud:townsquare:project']],
			[TypeFilterValueKey.Goal, ['ati:cloud:townsquare:goal']],
			[TypeFilterValueKey.Tag, ['ati:cloud:townsquare:tag']],
		]),
	],
	[
		ProductKeys.Bitbucket,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.Commit, ['ati:cloud:graph:commit']],
			[TypeFilterValueKey.PullRequest, ['ati:cloud:graph:pull-request']],
			[TypeFilterValueKey.Repository, ['ati:cloud:graph:repository']],
			[TypeFilterValueKey.Branch, ['ati:cloud:graph:branch']],
		]),
	],
	[
		ProductKeys.Assets,
		new Map<TypeFilterValueKey, EntityATI[]>([
			[TypeFilterValueKey.AssetsObject, ['ati:cloud:cmdb:object']],
			[TypeFilterValueKey.Schema, ['ati:cloud:cmdb:schema']],
			[TypeFilterValueKey.Type, ['ati:cloud:cmdb:type']],
		]),
	],
]);

export const CONFLUENCE_COMMENTS_AND_ATTACHMENTS_ENTITIES: EntityATI[] = [
	'ati:cloud:confluence:comment',
	'ati:cloud:confluence:attachment',
] as const;

/**
 * @returns an array of disabled ATIs in the 'All' types and 'All products' filter state, if there are any
 */
export const notRequestedATIsInAllSearch = (): EntityATI[] => [
	...CONFLUENCE_COMMENTS_AND_ATTACHMENTS_ENTITIES,
];

// Simple first-call-fills cache, export for testing only
// TODO: remove "export" when cleaning up rovo_search_in_jira_type_filter
export let cachedTypeMapping: UniversalTypeFilterMapping;

export const getTypeMapping: () => UniversalTypeFilterMapping = () => {
	if (cachedTypeMapping) {
		return cachedTypeMapping;
	}

	const mappings: UniversalTypeFilterMapping = Object.fromEntries(
		allTypeFilterValueKeys.map((TypeFilterValueKey) => {
			const baseMapping: TypeFilterMapping = {
				entities: {},
				products: [],
				subCategories: [],
			};
			return [TypeFilterValueKey as TypeFilterValueKey, baseMapping];
		}),
	) as UniversalTypeFilterMapping;

	// Add all the 1P products
	let typeFilter1PMapping = fg('rovo_search_in_jira_type_filter')
		? typeFilter1PMappingNew
		: typeFilter1PMappingOld;

	// eslint-disable-next-line @atlaskit/platform/no-preconditioning
	if (fg('jira_project_rename_to_spaces') && fg('rovo_search_in_jira_type_filter')) {
		typeFilter1PMapping = typeFilter1PMappingNewWithJiraSpaces;
	}

	typeFilter1PMapping.forEach((_, productKey1P) => {
		typeFilter1PMapping.get(productKey1P)?.forEach((_, typeFilterValueKey) => {
			mappings[typeFilterValueKey].products.push(productKey1P);
			mappings[typeFilterValueKey].entities[productKey1P] = typeFilter1PMapping
				.get(productKey1P)
				?.get(typeFilterValueKey);
		});
	});

	// Add all the 3P products
	ThirdPartyConfigs.forEach((thirdPartyConfig, productKey3P) => {
		const typeFilterConfigs = thirdPartyConfig?.typeFilterConfig;

		if (!typeFilterConfigs) {
			return;
		}

		typeFilterConfigs.forEach((typeFilterValue3PEntities, typeFilterValueKey) => {
			mappings[typeFilterValueKey].products.push(productKey3P);

			mappings[typeFilterValueKey].entities[productKey3P] = typeFilterValue3PEntities.entities3P;

			mappings[typeFilterValueKey].subCategories = [
				...mappings[typeFilterValueKey].subCategories,
				...typeFilterValue3PEntities.subTypes3P,
			];
			mappings[typeFilterValueKey].subCategories = [
				...new Set(mappings[typeFilterValueKey].subCategories),
			]; // dedupe
		});
	});

	cachedTypeMapping = mappings;
	return mappings;
};

/**
 * Registers a single forge connector's type mapping in the cache.
 * This should be called for each forge connector as it's being processed.
 * Similar to registerForgeConnectorIcon, this allows individual registration.
 * @param productKey - The forge connector product key
 * @param typeFilterConfig - The type filter config Map for this connector
 */
export const registerForgeConnectorTypeMapping = (
	productKey: string,
	typeFilterConfig: TypeFilterConfig3P,
) => {
	// Ensure cache is initialized before updating it
	if (!cachedTypeMapping) {
		getTypeMapping();
	}

	if (!typeFilterConfig || typeFilterConfig.size === 0) {
		return;
	}

	typeFilterConfig.forEach((typeFilterValue3PEntities, typeFilterValueKey) => {
		// Type guard: only process if it's a valid TypeFilterValueKey in our mapping
		if (!(typeFilterValueKey in cachedTypeMapping)) {
			return;
		}

		// Only add if not already present
		if (!cachedTypeMapping[typeFilterValueKey].products.includes(productKey)) {
			cachedTypeMapping[typeFilterValueKey].products.push(productKey);
		}

		cachedTypeMapping[typeFilterValueKey].entities[productKey] =
			typeFilterValue3PEntities.entities3P;

		cachedTypeMapping[typeFilterValueKey].subCategories = [
			...cachedTypeMapping[typeFilterValueKey].subCategories,
			...typeFilterValue3PEntities.subTypes3P,
		];
		cachedTypeMapping[typeFilterValueKey].subCategories = [
			...new Set(cachedTypeMapping[typeFilterValueKey].subCategories),
		]; // dedupe
	});
};
